import { Component } from '@theme/component';

/**
 * Sticky Add to Cart - Macchina
 * - Puppetta il vero ATC
 * - Aggiorna il titolo sticky: "Titolo parent · 90 - ottonata - Nero"
 * - Prezzo sync anche se <product-price> viene replaceWith()
 * - Niente observer globali
 */
class StickyAddToCartMacchinaComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  #observer = null;
  #footerObserver = null;
  #priceObserver = null;
  #buttonObserver = null;

  #targetButton = null;
  #priceSource = null;
  #isStuck = false;

  #titleEl = null;
  #baseTitle = '';

  // Grouped configurator
  #groupRoot = null;
  #groupObserver = null;
  #pollId = null;
  #rafPending = false;

  // Price node polling (product-price viene spesso sostituito)
  #pricePollId = null;

  connectedCallback() {
    super.connectedCallback();

    this.#titleEl = this.querySelector('.sticky-add-to-cart__title');
    this.#baseTitle = this.#titleEl?.textContent?.trim() || '';

    this.#bindTargets();
    this.#setupVisibilityObserver();
    this.#setupFooterObserver();
    this.#syncAll();

    // Leggi opzioni dal configuratore (max 6s di polling per attaccarsi al root)
    this.#initGroupedTitleSync();

    // Prezzo: sync anche quando <product-price> viene rimpiazzato
    this.#startPriceNodePolling();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#footerObserver?.disconnect();
    this.#priceObserver?.disconnect();
    this.#buttonObserver?.disconnect();

    this.#groupObserver?.disconnect();
    if (this.#groupRoot) {
      this.#groupRoot.removeEventListener('click', this.#onGroupedClick, true);
    }

    if (this.#pollId) {
      window.clearInterval(this.#pollId);
      this.#pollId = null;
    }

    if (this.#pricePollId) {
      window.clearInterval(this.#pricePollId);
      this.#pricePollId = null;
    }
  }

  handleAddToCartClick(event) {
    event.preventDefault();

    if (!this.#targetButton) {
      this.#bindTargets();
      this.#syncAvailability();
    }
    if (!this.#targetButton) return;

    const disabled =
      this.#targetButton.disabled ||
      this.#targetButton.getAttribute('aria-disabled') === 'true' ||
      this.getAttribute('data-variant-available') === 'false';

    if (disabled) return;

    try {
      this.#targetButton.dataset.puppet = 'true';
    } catch (_) {}

    this.#targetButton.click();
  }

  #bindTargets() {
    const scope = this.closest('.shopify-section') || document;

    const btn =
      scope.querySelector('product-form-component [ref="addToCartButton"]') ||
      scope.querySelector('.buy-buttons-block [ref="addToCartButton"]') ||
      scope.querySelector('form[action^="/cart/add"] button[type="submit"][name="add"]') ||
      scope.querySelector('form[action^="/cart/add"] button[type="submit"]') ||
      document.querySelector('product-form-component [ref="addToCartButton"]') ||
      document.querySelector('.buy-buttons-block [ref="addToCartButton"]') ||
      document.querySelector('form[action^="/cart/add"] button[type="submit"][name="add"]') ||
      document.querySelector('form[action^="/cart/add"] button[type="submit"]');

    this.#targetButton = btn instanceof HTMLButtonElement ? btn : null;

    this.#bindPriceSource();
    this.#setupButtonObserver();
  }

  #bindPriceSource() {
    const price =
      document.querySelector('product-price[data-product-price-main]') || document.querySelector('product-price');

    this.#priceSource = price instanceof Element ? price : null;
    this.#setupPriceObserver();
    this.#syncPrice();
  }

  #setupVisibilityObserver() {
    this.#observer?.disconnect();

    const target =
      this.#targetButton?.closest('.buy-buttons-block') ||
      this.#targetButton?.closest('form') ||
      this.#targetButton;

    if (!target) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        const shouldStick = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        if (shouldStick !== this.#isStuck) this.#setStuck(shouldStick);
      },
      { root: null, threshold: 0.1 }
    );

    this.#observer.observe(target);
  }

  #setupFooterObserver() {
    this.#footerObserver?.disconnect();

    const footer = document.querySelector('footer') || document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) this.#setStuck(false);
      },
      { root: null, threshold: 0 }
    );

    this.#footerObserver.observe(footer);
  }

  #setupPriceObserver() {
    this.#priceObserver?.disconnect();
    if (!this.#priceSource) return;

    this.#priceObserver = new MutationObserver(() => this.#syncPrice());
    this.#priceObserver.observe(this.#priceSource, { childList: true, subtree: true, characterData: true });
  }

  #setupButtonObserver() {
    this.#buttonObserver?.disconnect();
    if (!this.#targetButton) return;

    this.#buttonObserver = new MutationObserver(() => this.#syncAvailability());
    this.#buttonObserver.observe(this.#targetButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class'],
    });
  }

  #setStuck(stuck) {
    this.#isStuck = stuck;
    if (this.refs?.stickyBar) {
      this.refs.stickyBar.dataset.stuck = stuck ? 'true' : 'false';
    }
  }

  #syncAll() {
    this.#syncAvailability();
    this.#syncPrice();
  }

  #syncAvailability() {
    const available = this.#targetButton
      ? !this.#targetButton.disabled && this.#targetButton.getAttribute('aria-disabled') !== 'true'
      : true;

    this.setAttribute('data-variant-available', available ? 'true' : 'false');

    if (this.refs?.addToCartButton) {
      this.refs.addToCartButton.disabled = !available;
      if (!available) this.refs.addToCartButton.setAttribute('aria-disabled', 'true');
      else this.refs.addToCartButton.removeAttribute('aria-disabled');
    }
  }

  #syncPrice() {
    const priceContainer = this.querySelector('.sticky-add-to-cart__price');
    if (!priceContainer) return;

    // Se il nodo è stato rimpiazzato, riaggancia
    if (!this.#priceSource || !this.#priceSource.isConnected) {
      this.#bindPriceSource();
      return;
    }

    const html = this.#priceSource.innerHTML?.trim();
    if (html && priceContainer.innerHTML.trim() !== html) {
      priceContainer.innerHTML = html;
    }
  }

  #startPriceNodePolling() {
    this.#pricePollId = window.setInterval(() => {
      const current =
        document.querySelector('product-price[data-product-price-main]') || document.querySelector('product-price');
      if (!current) return;

      if (current !== this.#priceSource) {
        this.#priceSource = current;
        this.#setupPriceObserver();
        this.#syncPrice();
      }
    }, 250);
  }

  // ---------------------------
  // Grouped configurator → titolo: Parent · combinazione
  // ---------------------------

  #initGroupedTitleSync() {
    if (this.#attachGroupedRoot()) return;

    let tries = 0;
    this.#pollId = window.setInterval(() => {
      tries += 1;
      const ok = this.#attachGroupedRoot();
      if (ok || tries >= 20) {
        window.clearInterval(this.#pollId);
        this.#pollId = null;
      }
    }, 300);
  }

  #attachGroupedRoot() {
    const scope = this.closest('.shopify-section') || document;
    const root =
      scope.querySelector('[data-grouped-variants-root]') || document.querySelector('[data-grouped-variants-root]');

    if (!root) return false;
    if (this.#groupRoot === root) {
      this.#syncGroupedTitle();
      return true;
    }

    this.#groupObserver?.disconnect();
    if (this.#groupRoot) {
      this.#groupRoot.removeEventListener('click', this.#onGroupedClick, true);
    }

    this.#groupRoot = root;
    this.#groupRoot.addEventListener('click', this.#onGroupedClick, true);

    this.#groupObserver = new MutationObserver(() => {
      if (this.#rafPending) return;
      this.#rafPending = true;
      requestAnimationFrame(() => {
        this.#rafPending = false;
        this.#syncGroupedTitle();
      });
    });

    this.#groupObserver.observe(this.#groupRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-pressed'],
    });

    this.#syncGroupedTitle();
    return true;
  }

  #onGroupedClick = () => {
    window.setTimeout(() => {
      this.#syncGroupedTitle();
      this.#syncPrice();
    }, 0);

    window.setTimeout(() => this.#syncPrice(), 600);
  };

  #syncGroupedTitle() {
    if (!this.#titleEl) return;

    const p = this.#readSelectedValue('[data-role="pressacialda-options"]');
    const t = this.#readSelectedValue('[data-role="thermostato-options"]');
    const f = this.#readSelectedValue('[data-role="finitura-options"]');
    const c = this.#readSelectedColor('[data-role="colore-options"]');

    const parts = [p || t, f, c].map((x) => String(x || '').trim()).filter(Boolean);

    if (parts.length) {
      const options = parts.join(' - ');
      const parent = (this.#baseTitle || '').trim();
      const label = parent ? `${parent} · ${options}` : options;

      this.#titleEl.textContent = label;
      this.refs?.addToCartButton?.setAttribute('aria-label', label);
      this.refs?.addToCartButton?.setAttribute('title', label);
      return;
    }

    // fallback: titolo base
    this.#titleEl.textContent = this.#baseTitle || '';
    this.refs?.addToCartButton?.setAttribute('aria-label', this.#baseTitle || '');
    this.refs?.addToCartButton?.setAttribute('title', this.#baseTitle || '');
  }

  #readSelectedValue(roleSelector) {
    if (!this.#groupRoot) return '';

    const scope = this.#groupRoot.querySelector(roleSelector);
    if (!scope) return '';

    const byClass =
      scope.querySelector('.variant-group-picker__option.is-selected') || scope.querySelector('.is-selected');

    if (byClass) {
      const dv = byClass.getAttribute('data-value');
      if (dv) return dv.trim();
      const txt = byClass.textContent?.trim();
      if (txt) return txt;
    }

    const pressed = scope.querySelector('button[aria-pressed="true"]');
    if (pressed) {
      const dv = pressed.getAttribute('data-value');
      if (dv) return dv.trim();
      const txt = pressed.textContent?.trim();
      if (txt) return txt;
    }

    const checked = scope.querySelector('input:checked');
    if (checked) {
      const v = checked.value?.trim();
      if (v) return v;
      const label = checked.closest('label');
      const txt = label?.textContent?.trim();
      if (txt) return txt;
    }

    return '';
  }

  #readSelectedColor(roleSelector) {
    if (!this.#groupRoot) return '';

    const scope = this.#groupRoot.querySelector(roleSelector);
    if (!scope) return '';

    const selected =
      scope.querySelector('.variant-group-picker__option--color.is-selected') ||
      scope.querySelector('.variant-group-picker__option.is-selected') ||
      scope.querySelector('.is-selected');

    if (selected) {
      const dc = selected.getAttribute('data-color');
      if (dc) return dc.trim();
      const dv = selected.getAttribute('data-value');
      if (dv) return dv.trim();
      const aria = selected.getAttribute('aria-label');
      if (aria) return aria.trim();
      const txt = selected.textContent?.trim();
      if (txt) return txt;
    }

    const pressed = scope.querySelector('button[aria-pressed="true"]');
    if (pressed) {
      const dc = pressed.getAttribute('data-color');
      if (dc) return dc.trim();
      const dv = pressed.getAttribute('data-value');
      if (dv) return dv.trim();
      const aria = pressed.getAttribute('aria-label');
      if (aria) return aria.trim();
    }

    return '';
  }
}

if (!customElements.get('sticky-add-to-cart-macchina')) {
  customElements.define('sticky-add-to-cart-macchina', StickyAddToCartMacchinaComponent);
}
